import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleArrowUp, X } from "lucide-react";
import { InfoBanner } from "@ds/patterns/info-banner";
import { Button } from "@ds/ui/button";
import { log } from "@/app/observability";
import { openExternal, preferences } from "@/app/platform";
import type { MinimumVersion } from "@shared/contracts";

/**
 * THE NUDGE, and it is deliberately NOT a wall.
 *
 * Above the floor the build still works, so interrupting someone is not earned. But without
 * anything here the only tool for "get people onto the new build" is the wall, which is the
 * step AFTER this one — and a project whose only move is the expensive one postpones it,
 * which is how a destructive migration ends up waiting forever behind a floor nobody dares
 * raise.
 *
 * DISMISSAL IS KEYED ON THE VERSION THAT PROMPTED IT, which is the whole reason it is
 * stored rather than held in a ref. Per-session forgetting trains people to swat it; never
 * asking again means someone who dismissed once in March hears nothing about a release in
 * September. Storing the recommended version splits the difference: this recommendation is
 * answered, the next one is not.
 *
 * WHERE IT IS STORED: `app/platform/preferences.ts` — `UserDefaults` on device, which iOS
 * does not reclaim. NOT `app/platform/storage.ts`, which is the Keychain and exists for
 * credentials; and not the web view's own `localStorage`, which WebKit may evict, turning a
 * dismissal into a banner that quietly comes back.
 */
const DISMISSED_KEY = "app.update-nudge-dismissed";

export function UpdateBanner({ info }: { info: MinimumVersion }) {
  const { t } = useTranslation();
  /** `undefined` while the stored answer is still being read — see the guard below. */
  const [dismissedVersion, setDismissedVersion] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void preferences
      .get(DISMISSED_KEY)
      /**
       * A REFUSED READ MEANS "NOT DISMISSED", NOT "STAY QUIET". The web path answers null
       * rather than throwing, but the `UserDefaults` path is a plugin call and can reject —
       * and without this the state would sit at `undefined` for the life of the process, so
       * the nudge would never appear again and nothing would say why. Showing a banner
       * someone has already closed is the cheaper of the two mistakes.
       */
      .catch(() => {
        log.warn("stored update-nudge dismissal could not be read; treating it as unanswered");
        return null;
      })
      .then((stored) => {
        if (!cancelled) setDismissedVersion(stored);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Reading the store is a round trip through a plugin, so the first frame does not know the
   * answer yet. Rendering optimistically would flash the banner on every single launch for
   * someone who already dismissed it — the most visible possible way to ignore a dismissal.
   */
  if (dismissedVersion === undefined) return null;
  if (dismissedVersion === info.recommended) return null;

  function dismiss() {
    setDismissedVersion(info.recommended);
    void preferences.set(DISMISSED_KEY, info.recommended).catch(() => {
      // The worst case is the banner returning next launch, so this is a line in the log
      // rather than something to put in front of the user for a notice they just closed.
      log.warn("update nudge dismissal could not be stored");
    });
  }

  /**
   * ⚠️ A DECISION MADE ON THE DESIGN SYSTEM'S BEHALF, twice over.
   *
   * The system leaves `13.5 Offline`, `10.13 Error screen`, `9.11 Stacking order` and
   * `9.12 Overlay root` undecided, so there is no sanctioned component and no sanctioned
   * layer for a notice like this. Rather than invent a second visual language it reuses
   * `InfoBanner`, exactly as the offline banner already does.
   *
   * PINNED TO THE BOTTOM, on the line the shell already keeps clear. The top of the frame is
   * where the offline banner sits in flow, and two notices stacking there is how a heading
   * ends up off screen. `AppShell` keeps phone content clear of `--spacing(28)` plus the home
   * indicator so its floating navigation never covers anything, so sitting on that same line
   * puts this above the navigation without measuring a component this project does not own —
   * if the system's bar changes height, that number changes with it.
   *
   * ⚠️ THAT RESERVE IS NOT UNIVERSAL, and the number is right for the wrong reason on one
   * screen: `routes/sign-in.tsx` renders outside `AppShell`, so it has no floating navigation
   * and no reserve, and the banner floats there with empty space beneath it that corresponds
   * to no chrome. Harmless, and preferable to two positioning rules.
   *
   * ⚠️ `z-30` IS BORROWED, NOT OWNED. It is the layer the shell's own nav and footer wrappers
   * use, written as a bare literal in `@ds/patterns/app-shell` and exported as nothing, so
   * this is a coupling to a value the design system does not publish. Today the two never
   * overlap — the reserve above keeps them apart — but if an install moves that number,
   * nothing here goes red. `Toaster` in app/providers dodges the same problem by sitting at
   * `z-index: 999999999`, which is not an option for something that must sit UNDER a toast.
   */
  return (
    <div
      role="status"
      /**
       * `data-slot` so the keyboard lift comes from the one rule in `styles/native.css` that
       * already owns it for the shell's footer and tab bar, rather than a fourth hand-written
       * transform with its own easing.
       */
      data-slot="update-banner"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-4 pb-[calc(--spacing(28)+env(safe-area-inset-bottom))]"
    >
      <div className="pointer-events-auto relative w-full max-w-md">
        <InfoBanner
          className="border border-border pr-12"
          leading={<CircleArrowUp />}
          title={t("update.recommendedTitle")}
          description={t("update.recommendedBody")}
          action={
            /*
             * The OUTLINED variant, not the filled one. A screen underneath this may already
             * own the single primary action the system allows — the note composer's submit and
             * the sign-in button are both filled pills — and a nudge nobody asked for is not
             * the thing that gets to outrank them.
             */
            <Button variant="secondary" size="sm" onClick={() => void openExternal(info.storeUrl.ios)}>
              {t("update.openStore")}
            </Button>
          }
        />
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("update.dismiss")}
          onClick={dismiss}
          className="absolute top-1 right-1"
        >
          <X />
        </Button>
      </div>
    </div>
  );
}
